import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrashAlt, faChevronDown, faChevronUp, faPlus, faImages, faHotel, faLocation, faSort, faSortUp, faSortDown, faGear } from "@fortawesome/free-solid-svg-icons";

import AddHotelForm from "./AddHotelForm";
import EditHotelForm from "./EditHotelForm";
import AddRoom from "./AddRoom";
import EditRoom from "./EditRoom";
import AssignRooms from "./AssignRooms";
import ConfigurationList from "./ConfigurationList";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { formatDate } from "../../utils/date";
import "../css/HotelList.css";
import { GoldLoader } from "../shared/Loaders";
import "../css/ParticipantList.css";

const HotelList = () => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [global_hotels, setGlobalHotels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [galleryHotel, setGalleryHotel] = useState(null);
  const [selectedHotelForRoom, setSelectedHotelForRoom] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [assignModalRoom, setAssignModalRoom] = useState(null);
  const [configRoom, setConfigRoom] = useState(null);
  const [assignModalHotel, setAssignModalHotel] = useState(null);
  const [showExistingHotelModal, setShowExistingHotelModal] = useState(false);
  const [selectedExistingHotelId, setSelectedExistingHotelId] = useState("");
  const [assignedRooms, setAssignedRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [loadingHotels, setLoadingHotels] = useState(true);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3500);
  };
  const [expandedHotelId, setExpandedHotelId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const API_URL = process.env.REACT_APP_API_URL;

  // ================= FETCH =================
  const fetchGlobalHotels = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/global-hotels`);
      if (Array.isArray(res.data)) {
        const sorted = [...res.data].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        setGlobalHotels(sorted);
      }
    } catch (err) {
      console.error("Failed to fetch global hotels", err);
    } finally {
      setLoadingHotels(false);
    }
  };

  const fetchHotels = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/hotels`);
      if (Array.isArray(res.data)) setHotels(res.data);
    } catch (err) {
      console.error("Failed to fetch hotels", err);
    }
  };

  const fetchRooms = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/rooms`);
      if (Array.isArray(res.data)) setRooms(res.data);
    } catch (err) {
      console.error("Failed to fetch rooms", err);
    }
  };

  const fetchCompetitions = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/competitions`);
      const data = res.data?.data || res.data?.competitions || res.data;
      setCompetitions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch competitions", err);
    }
  };

  const fetchAssignedRooms = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/assign-rooms`);
      if (Array.isArray(res.data)) {
        setAssignedRooms(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch assigned rooms", err);
    }
  };

  useEffect(() => {
    fetchHotels();
    fetchGlobalHotels();
    fetchRooms();
    fetchCompetitions();
    fetchAssignedRooms();
  }, []);

  // ================= DATA LOGIC =================

  const activeCompIds = competitions
    .filter(c => c.status === true || String(c.status) === "true")
    .map(c => String(c._id));

  const hotelsInActiveComps = hotels.filter(h => 
    h.competition_id && activeCompIds.includes(String(h.competition_id?._id || h.competition_id))
  );

  const hotelsInInactiveComps = hotels.filter(h => 
    !h.competition_id || !activeCompIds.includes(String(h.competition_id?._id || h.competition_id))
  );

  // Filter global hotels by search term
  const filteredGlobalHotels = global_hotels.filter((hotel) =>
    (hotel.global_hotel_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (hotel.global_address || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const sortedGlobalHotels = sortConfig.key
    ? [...filteredGlobalHotels].sort((a, b) => {
        const aVal = (a[sortConfig.key] || '').toLowerCase();
        const bVal = (b[sortConfig.key] || '').toLowerCase();
        const result = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? result : -result;
      })
    : filteredGlobalHotels;

  // Pagination calculations
  const effectivePerPage = itemsPerPage >= sortedGlobalHotels.length ? Math.max(sortedGlobalHotels.length, 1) : itemsPerPage;
  const totalPages = Math.max(Math.ceil(sortedGlobalHotels.length / effectivePerPage), 1);
  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentHotels = sortedGlobalHotels.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;
    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  const toggleExpand = (id) => {
    setExpandedHotelId(expandedHotelId === id ? null : id);
  };

  const getHotelRooms = (hotelId) => {
    const relatedHotels = hotels.filter((h) => {
      const gid = h.global_hotel_id?._id || h.global_hotel_id;
      return String(gid) === String(hotelId);
    });
    
    let allRooms = [];
    relatedHotels.forEach((hotel) => {
      const hotelRooms = rooms.filter(
        (room) =>
          Array.isArray(room.hotel_id) &&
          room.hotel_id.some((hid) => String(hid?._id || hid) === String(hotel._id))
      );
      hotelRooms.forEach((room) => {
        allRooms.push({ ...room, relatedHotel: hotel });
      });
    });
    return allRooms.sort((a, b) =>
      (a.room_name || a.room_type || "").localeCompare(b.room_name || b.room_type || "", undefined, { sensitivity: "base" })
    );
  };

  const getRelatedHotels = (globalHotelId) => {
    return hotels.filter((h) => {
      const gid = h.global_hotel_id?._id || h.global_hotel_id;
      return String(gid) === String(globalHotelId);
    });
  };

  const getIgfAssigned = (hotelId, roomId) => {
    return assignedRooms
      .filter((a) => {
        const assignHotelId = a.hotel_id?._id || a.hotel_id;
        const assignRoomId = a.room_id?._id || a.room_id;
        return String(assignHotelId) === String(hotelId) && String(assignRoomId) === String(roomId);
      })
      .reduce((sum, a) => sum + Number(a.number_of_rooms || 0), 0);
  };

  // ================= DELETE =================
  const handleDelete = (hotel) => { setDeleteError(''); setDeleteTarget({ id: hotel._id, name: hotel.hotel_name || 'Hotel', type: 'hotel' }); };
  const handleDeleteRoom = (room) => { setDeleteError(''); setDeleteTarget({ id: room._id, name: room.room_name || room.room_type || 'Room', type: 'room' }); };

  const confirmDelete = async () => {
    const { id, type } = deleteTarget;
    try {
      if (type === 'hotel') {
        await axios.delete(`${API_URL}/ems/global-hotels/${id}`);
        setGlobalHotels((prev) => prev.filter((g) => g._id !== id));
      } else {
        await axios.delete(`${API_URL}/ems/rooms/${id}`);
        setRooms((prev) => prev.filter((r) => r._id !== id));
      }
      const label = deleteTarget.type === 'hotel' ? 'Hotel' : 'Room';
      setDeleteTarget(null);
      setDeleteError('');
      showToast(`${label} deleted successfully`);
    } catch (err) {
      setDeleteError(err.response?.data?.message || "Delete failed");
    }
  };

  const handleAddExistingHotel = async () => {
  if (!selectedExistingHotelId) {
    alert("Please select a hotel");
    return;
  }

  try {
    await axios.put(
      `${API_URL}/ems/hotels/add-to-active-competition/${selectedExistingHotelId}`
    );

    alert("Hotel added to active competition successfully");
    setShowExistingHotelModal(false);
    setSelectedExistingHotelId("");
    fetchHotels();
  } catch (err) {
    console.error("Failed to add existing hotel", err);
    alert(err.response?.data?.message || "Failed to add hotel");
  }
};


  // ================= UI =================
  if (loadingHotels) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="ems-hotels-page p-4">
      {/* Header with Search on left and Buttons on right */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 ems-list-toolbar">
        <div className="d-flex align-items-center w-25">
          <div className="me-2" style={{ flex: "1" }}>
            <input
              type="text"
              className="form-control form-control-sm search-input"
              placeholder="Search hotels..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search hotels"
              style={{ border: "1px solid #d1cfcf" }}
            />
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn"
            style={{ background: "white", color: "black", border: "1px solid black", padding: "8px 16px", borderRadius: "5px", fontWeight: 600 }}
            onClick={() => setShowExistingHotelModal(true)}
          >
            + Add Existing Hotel
          </button>
          <button
            className="btn"
            style={{ background: "black", color: "white", padding: "8px 16px", borderRadius: "5px", border: "none", fontWeight: 600 }}
            onClick={() => setShowModal(true)}
          >
            + Add Hotel
          </button>
        </div>
      </div>

      {/* Table Header */}
      <div className="hotel-table-header">
        <div
          className={`sort-header ${sortConfig.key === 'global_hotel_name' ? 'sorted' : ''}`}
          onClick={() => toggleSort('global_hotel_name')}
        >
          Hotel Name <FontAwesomeIcon icon={sortIcon('global_hotel_name')} className="sort-icon" />
        </div>
        <div
          className={`sort-header ${sortConfig.key === 'global_address' ? 'sorted' : ''}`}
          onClick={() => toggleSort('global_address')}
        >
          Address <FontAwesomeIcon icon={sortIcon('global_address')} className="sort-icon" />
        </div>
        <div>Phone</div>
        <div>Rooms</div>
        <div>Actions</div>
      </div>

      {loadingHotels ? (
        <GoldLoader label="Loading..." variant="section" />
      ) : sortedGlobalHotels.length === 0 ? (
        <div className="p-4 text-center text-muted" style={{ background: "#FFFBF4" }}>
          {searchTerm ? "No hotels match your search." : "No hotels available."}
        </div>
      ) : (
        currentHotels.map((global_hotel) => {
          const relatedHotels = getRelatedHotels(global_hotel._id);
          const hotelRooms = getHotelRooms(global_hotel._id);
          const isExpanded = expandedHotelId === global_hotel._id;

          return (
            <React.Fragment key={global_hotel._id}>
              {/* Hotel Row */}
              <div className="hotel-table-row">
                <div
                  onClick={() => toggleExpand(global_hotel._id)}
                  style={{ cursor: "pointer", fontWeight: 500 }}
                >
                  <FontAwesomeIcon 
                    icon={isExpanded ? faChevronUp : faChevronDown} 
                    style={{ marginRight: "8px", fontSize: "11px" }} 
                  />
                  {global_hotel.global_hotel_name || "-"}
                </div>
                <div>{global_hotel.global_address || "-"}</div>
                <div>{global_hotel.contact_phone || "-"}</div>
                <div>{hotelRooms.length} room type(s)</div>
                <div className="hotel-actions">
                  <button
                    className="btn btn-sm action-btn"
                    onClick={() => setGalleryHotel(global_hotel)}
                    title="Photo Gallery"
                  >
                    <FontAwesomeIcon icon={faImages} />
                  </button>
                  {relatedHotels.map((hotel) => (
                    <button
                      key={hotel._id}
                      className="btn btn-sm action-btn"
                      onClick={() => setSelectedHotelForRoom(hotel)}
                      title="Add a Room"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  ))}
                  <button
                    className="btn btn-sm action-btn"
                    onClick={() => setEditingHotel(global_hotel)}
                    title="Edit Hotel"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button
                    className="btn btn-sm action-btn"
                    onClick={() => handleDelete(global_hotel)}
                    title="Delete Hotel"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              </div>

              {/* Expanded Room Details */}
              {isExpanded && (
                <div className="hotel-expanded-details">
                  {hotelRooms.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: "13px", padding: "10px 15px" }}>No rooms assigned.</p>
                  ) : (
                    <>
                      <div className="room-table-header">
                        <div>Room Name</div>
                        <div>Date Range</div>
                        <div>Reserved</div>
                        <div>Assigned</div>
                        <div>Actions</div>
                      </div>
                      {hotelRooms.map((room) => {
                        const igfAssigned = getIgfAssigned(room.relatedHotel._id, room._id);
                        return (
                          <div key={room._id} className="room-table-row">
                            <div>{room.room_name || room.room_type || "-"}</div>
                            <div>{formatDate(room.from_date)} – {formatDate(room.to_date)}</div>
                            <div>{room.no_of_rooms || 0}</div>
                            <div>{igfAssigned}</div>
                            <div className="room-actions">
                              <button
                                className="btn btn-sm"
                                style={{ background: "#D9A128", color: "#fff", fontSize: "11px", padding: "3px 8px", marginRight: "5px" }}
                                onClick={() => navigate(`/ems/bookings?hotel_id=${room.relatedHotel._id}&room_id=${room._id}`)}
                              >
                                Assign
                              </button>
                              <button
                                className="btn btn-sm action-btn"
                                onClick={() => setConfigRoom(room)}
                                title="Configurations"
                              >
                                <FontAwesomeIcon icon={faGear} />
                              </button>
                              <button
                                className="btn btn-sm action-btn"
                                onClick={() => setEditingRoom(room)}
                                title="Edit Room"
                              >
                                <FontAwesomeIcon icon={faPen} />
                              </button>
                              <button
                                className="btn btn-sm action-btn"
                                onClick={() => handleDeleteRoom(room)}
                                title="Delete Room"
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })
      )}

      {/* Pagination inside table */}
      {sortedGlobalHotels.length > 0 && (
        <div className="hotel-pagination">
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: "13px", color: "#666" }}>Show</span>
            <select
              className="form-select form-select-sm"
              style={{ width: "auto", fontSize: "13px" }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="pagination-box">
            <button
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>

            {getVisiblePages().map((num) => (
              <button
                key={num}
                className={`pagination-btn ${num === currentPage ? "active" : ""}`}
                onClick={() => setCurrentPage(num)}
              >
                {num}
              </button>
            ))}

            <button
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showModal && <AddHotelForm onHotelAdded={() => { setShowModal(false); fetchGlobalHotels(); }} onClose={() => setShowModal(false)} />}
      {editingHotel && <EditHotelForm hotel={editingHotel} onClose={() => setEditingHotel(null)} onUpdated={() => { fetchHotels(); fetchGlobalHotels(); }} />}
      {selectedHotelForRoom && <AddRoom selectedHotel={selectedHotelForRoom} onAdded={() => { setSelectedHotelForRoom(null); fetchRooms(); }} onClose={() => setSelectedHotelForRoom(null)} />}
      {editingRoom && (
        <EditRoom
          room={editingRoom}
          global_hotels={global_hotels.filter(h =>
            Array.isArray(editingRoom.global_hotel_id) &&
            editingRoom.global_hotel_id.some(rh =>
              typeof rh === "object" ? rh._id === h._id : rh === h._id
            )
          )}
          onClose={() => setEditingRoom(null)}
          onUpdated={fetchRooms}
        />
      )}
      {galleryHotel && (
         <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }}>
           <div style={{ background: "#fff", padding: "30px", width: "80%", maxWidth: "1200px", borderRadius: "12px", position: "relative" }}>
             <button onClick={() => setGalleryHotel(null)} style={{ position: "absolute", top: "15px", right: "20px", fontSize: "26px", border: "none", background: "none", cursor: "pointer", fontWeight: "bold" }}>&times;</button>
             <h2 style={{ marginBottom: "20px", fontWeight: "600" }}>Photo Gallery</h2>
             <div>
               <h4><FontAwesomeIcon icon={faHotel} /> {galleryHotel.global_hotel_name}</h4>
               <p><FontAwesomeIcon icon={faLocation} /> {galleryHotel.global_address}</p>
                <div style={{ display: "flex", justifyContent: "center", background: "#ddd", height: "400px", borderRadius: "10px", overflow: "hidden" }}>
              {galleryHotel?.photo_url ? (
                <img
                  src={`${API_URL}/ems${galleryHotel.photo_url}`}
                  alt={galleryHotel.global_hotel_name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                  <FontAwesomeIcon icon={faHotel} style={{ fontSize: "60px", color: "#aaa" }} />
                  <p className="mt-2 text-muted">No Image Available</p>
                </div>
              )}
            </div>
             </div>
             
           </div>
         </div>
      )}
      {configRoom && (
        <ConfigurationList
          room={configRoom}
          onClose={() => setConfigRoom(null)}
        />
      )}
      {assignModalRoom && assignModalHotel && (
        <AssignRooms hotel={assignModalHotel} room={assignModalRoom} onClose={() => { setAssignModalRoom(null); setAssignModalHotel(null); }} onAssigned={fetchRooms} />
      )}

      {showExistingHotelModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", padding: "24px", width: "500px", borderRadius: "10px", position: "relative" }}>
            <button onClick={() => setShowExistingHotelModal(false)} style={{ position: "absolute", top: "10px", right: "14px", border: "none", background: "none", fontSize: "22px" }}>&times;</button>
            <h4>Add Existing Hotel</h4>
            <select 
              style={{ width: "100%", margin: "15px 0", padding: "8px" }}
              value={selectedExistingHotelId}
              onChange={(e) => setSelectedExistingHotelId(e.target.value)}
            >
              <option value="">Select Hotel</option>
              
              {/* Grouping available hotels first */}
                {hotelsInInactiveComps.map((h) => {
                  const hotelName = h.global_hotel_id?.global_hotel_name || "Unnamed Hotel";
                  return <option key={h._id} value={h._id}>{hotelName}</option>;
                })}

              {/* Grouping active hotels as disabled */}
                {hotelsInActiveComps.map((h) => {
                  const hotelName = h.global_hotel_id?.global_hotel_name || "Unnamed Hotel";
                  return <option key={h._id} value={h._id} disabled style={{ color: "gray" }}>{hotelName} </option>;
                })}
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button onClick={() => setShowExistingHotelModal(false)} className="btn">Cancel</button>
              <button className="btn btn-warning" onClick={handleAddExistingHotel} disabled={!selectedExistingHotelId}>Add</button>
            </div>
          </div>
        </div>
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          name={deleteTarget.name}
          error={deleteError}
          onConfirm={confirmDelete}
          onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
        />
      )}
      {toastMsg && <div className="hotel-toast">{toastMsg}</div>}
    </div>
  );
};

export default HotelList;